import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Ticket as TicketIcon, User, Lock, LogOut, Calendar, Clock, CheckCircle2,
  AlertCircle, Loader2, Mail, Phone, ExternalLink, Home, CreditCard, Baby,
  FileSignature, MessageSquare, Pencil,
} from "lucide-react";

import { useUserAuth, formatApiErrorDetail } from "../../context/UserAuthContext";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { API_URL } from "../../config";

const StatusBadge = ({ status }) => {
  const map = {
    paid: { label: "Pago", cls: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30" },
    pending: { label: "Pendente", cls: "bg-amber-500/15 text-amber-300 border-amber-500/30" },
    confirmed: { label: "Confirmado", cls: "bg-blue-500/15 text-blue-300 border-blue-500/30" },
    installments: { label: "Em Prestações", cls: "bg-amber-500/15 text-amber-300 border-amber-500/30" },
  };
  const m = map[status] || { label: status || "—", cls: "bg-zinc-500/15 text-zinc-300 border-zinc-500/30" };
  return <span className={`text-xs px-2 py-0.5 rounded-full border font-medium ${m.cls}`}>{m.label}</span>;
};

const PlanProgressBar = ({ percent }) => (
  <div className="w-full bg-white/5 rounded-full h-1.5 overflow-hidden">
    <div className="h-full bg-amber-400 rounded-full transition-all" style={{ width: `${Math.min(100, percent)}%` }} />
  </div>
);

const PayInstallmentDialog = ({ ticket, open, onClose, onPaid }) => {
  const [amount, setAmount] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open && ticket) {
      // Default amount: try minimum installment, fallback to remaining
      const minInst = ticket.plan_config?.min_installment_amount ?? 10;
      const remaining = ticket.remaining_due;
      // eslint-disable-next-line
      setAmount(Math.min(minInst, remaining).toFixed(2));
    }
  }, [open, ticket]);

  if (!ticket) return null;

  const total = ticket.total_due;
  const paid = ticket.total_paid;
  const remaining = ticket.remaining_due;
  const minInst = ticket.plan_config?.min_installment_amount ?? 10;
  const numericAmount = parseFloat(amount) || 0;
  const isFinal = numericAmount >= remaining - 0.005;
  const belowMin = !isFinal && numericAmount < minInst - 0.005;
  const overRemaining = numericAmount > remaining + 0.005;
  const invalid = numericAmount <= 0 || belowMin || overRemaining;

  const handlePay = async () => {
    setSubmitting(true);
    try {
      const { data } = await axios.post(
        `${API_URL}/api/stripe/create-installment-checkout`,
        {
          registration_id: ticket.id,
          amount: numericAmount,
          origin_url: window.location.origin,
        },
        { withCredentials: true }
      );
      if (data.url) {
        window.location.href = data.url;
      } else {
        toast.error("URL Stripe não recebido");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao iniciar pagamento");
    } finally {
      setSubmitting(false);
    }
  };

  return open ? (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="pay-installment-dialog">
      <div className="bg-zinc-950 border border-amber-500/30 rounded-2xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-lg font-bold text-white mb-1">Pagar prestação</h3>
        <p className="text-sm text-zinc-400 mb-4">{ticket.ticket_type_name} · {ticket.first_name} {ticket.last_name}</p>

        <div className="grid grid-cols-3 gap-2 mb-4">
          <div className="bg-white/5 rounded-lg p-2 text-center">
            <p className="text-[10px] text-zinc-500 uppercase">Total</p>
            <p className="text-sm text-white font-medium">{total.toFixed(2)}€</p>
          </div>
          <div className="bg-emerald-500/10 rounded-lg p-2 text-center">
            <p className="text-[10px] text-zinc-500 uppercase">Pago</p>
            <p className="text-sm text-emerald-300 font-medium">{paid.toFixed(2)}€</p>
          </div>
          <div className="bg-amber-500/10 rounded-lg p-2 text-center">
            <p className="text-[10px] text-zinc-500 uppercase">Restante</p>
            <p className="text-sm text-amber-300 font-medium">{remaining.toFixed(2)}€</p>
          </div>
        </div>

        <Label className="text-zinc-300 text-sm">Valor a pagar agora (€)</Label>
        <Input
          type="number"
          step="0.01"
          min={Math.min(minInst, remaining)}
          max={remaining}
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          className="input-dark mt-1.5"
          data-testid="pay-installment-amount"
          autoFocus
        />
        <div className="flex gap-1.5 mt-2 flex-wrap">
          <button type="button" onClick={() => setAmount(remaining.toFixed(2))} className="text-xs px-2 py-1 rounded-md bg-emerald-500/15 text-emerald-300 hover:bg-emerald-500/25" data-testid="pay-installment-all">
            Pagar tudo ({remaining.toFixed(2)}€)
          </button>
          {[2, 3].map((n) => {
            const v = remaining / n;
            return (
              <button key={n} type="button" onClick={() => setAmount(v.toFixed(2))} className="text-xs px-2 py-1 rounded-md bg-white/5 text-zinc-300 hover:bg-white/10">
                ÷{n} ({v.toFixed(2)}€)
              </button>
            );
          })}
        </div>
        <p className="text-[11px] text-zinc-500 mt-2">
          {isFinal ? "Esta é a prestação final — o bilhete fica disponível após o pagamento."
           : belowMin ? <span className="text-amber-300">Mínimo {minInst.toFixed(2)}€ por prestação (ou pague o restante).</span>
           : overRemaining ? <span className="text-red-300">Excede o restante ({remaining.toFixed(2)}€).</span>
           : "Após este pagamento ainda restará pagar."}
        </p>

        <div className="flex justify-end gap-2 mt-5">
          <Button variant="outline" className="border-white/10 text-zinc-300" onClick={onClose}>Cancelar</Button>
          <Button onClick={handlePay} disabled={invalid || submitting} className="btn-primary" data-testid="pay-installment-submit">
            {submitting ? "..." : "Pagar via Stripe"}
          </Button>
        </div>
      </div>
    </div>
  ) : null;
};


const SignPlanDialog = ({ plan, open, onClose, onSigned }) => {
  const [typedName, setTypedName] = useState("");
  const [accepted, setAccepted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open && plan) {
      setTypedName(`${plan.first_name || ""} ${plan.last_name || ""}`.trim());
      setAccepted(false);
    }
  }, [open, plan]);

  if (!plan) return null;
  const schedule = plan.payment_schedule || [];
  const total = schedule.reduce((s, r) => s + Number(r.amount || 0), 0);
  const canSubmit = accepted && typedName.trim().length >= 3;

  const handleSign = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      await axios.post(
        `${API_URL}/api/users/me/tickets/${plan.id}/sign-agreement`,
        { typed_name: typedName.trim(), accepted: true },
        { withCredentials: true },
      );
      toast.success("Plano assinado. Obrigado!");
      onSigned();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao assinar");
    } finally { setSubmitting(false); }
  };

  return open ? (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" data-testid="sign-plan-dialog">
      <div className="bg-zinc-950 border border-sky-500/30 rounded-2xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center gap-2 mb-2">
          <FileSignature className="w-6 h-6 text-sky-300" />
          <h3 className="text-lg font-semibold text-white">Confirmar plano de pagamento</h3>
        </div>
        <p className="text-zinc-400 text-sm mb-4">
          Ao assinar, confirmas que aceitas o cronograma abaixo e comprometes-te a pagar cada prestação até à data indicada.
        </p>

        <div className="bg-white/[0.03] border border-white/5 rounded-lg overflow-hidden mb-4">
          <table className="w-full text-sm">
            <thead className="bg-white/5">
              <tr>
                <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider text-zinc-500">Nº</th>
                <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider text-zinc-500">Data</th>
                <th className="text-right px-3 py-2 text-[10px] uppercase tracking-wider text-zinc-500">Valor</th>
              </tr>
            </thead>
            <tbody>
              {schedule.map((s) => (
                <tr key={s.id || s.sequence} className="border-t border-white/5">
                  <td className="px-3 py-2 text-sky-300 font-semibold">#{s.sequence}</td>
                  <td className="px-3 py-2 text-white">{s.due_date ? new Date(s.due_date + "T00:00:00Z").toLocaleDateString("pt-PT") : "—"}</td>
                  <td className="px-3 py-2 text-right text-amber-300 font-mono">{Number(s.amount || 0).toFixed(2)}€</td>
                </tr>
              ))}
              <tr className="border-t-2 border-white/10 bg-white/[0.03]">
                <td colSpan={2} className="px-3 py-2 text-zinc-400 uppercase text-xs">Total</td>
                <td className="px-3 py-2 text-right text-white font-bold">{total.toFixed(2)}€</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div className="space-y-3">
          <label className="flex items-start gap-3 bg-sky-500/5 border border-sky-500/20 rounded-lg p-3 cursor-pointer">
            <input
              type="checkbox"
              checked={accepted}
              onChange={(e) => setAccepted(e.target.checked)}
              className="mt-0.5 w-4 h-4 accent-sky-500 cursor-pointer"
              data-testid="sign-plan-accept"
            />
            <span className="text-sky-100 text-sm">
              Aceito o plano de pagamento acima e comprometo-me a cumprir todas as datas indicadas.
            </span>
          </label>
          <div>
            <Label className="text-zinc-300 text-sm">O teu nome completo (assinatura)</Label>
            <Input
              value={typedName}
              onChange={(e) => setTypedName(e.target.value)}
              placeholder="Escreve o teu nome"
              className="input-dark mt-1.5"
              data-testid="sign-plan-name"
            />
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-5">
          <Button variant="outline" className="border-white/10 text-zinc-300" onClick={onClose}>Cancelar</Button>
          <Button
            onClick={handleSign}
            disabled={!canSubmit || submitting}
            className="bg-sky-500 hover:bg-sky-600 text-white"
            data-testid="sign-plan-submit"
          >
            {submitting ? "..." : "Assinar plano"}
          </Button>
        </div>
      </div>
    </div>
  ) : null;
};


const RequestChangeDialog = ({ data, open, onClose, onSent }) => {
  const [date, setDate] = useState("");
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open && data?.item) {
      setDate(data.item.due_date || "");
      setAmount("");
      setReason("");
    }
  }, [open, data]);

  if (!data) return null;
  const changedDate = date && date !== data.item.due_date;
  const parsedAmount = parseFloat(amount);
  const changedAmount = !Number.isNaN(parsedAmount) && parsedAmount > 0 && parsedAmount !== Number(data.item.amount);
  const canSubmit = changedDate || changedAmount;

  const submit = async () => {
    if (!canSubmit) return toast.error("Indica uma nova data ou valor diferente");
    setSubmitting(true);
    try {
      await axios.post(
        `${API_URL}/api/users/me/tickets/${data.plan.id}/schedule/${data.item.id}/request-change`,
        {
          proposed_date: changedDate ? date : null,
          proposed_amount: changedAmount ? parsedAmount : null,
          reason: reason || null,
        },
        { withCredentials: true },
      );
      toast.success("Pedido enviado ao admin");
      onSent();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao enviar pedido");
    } finally { setSubmitting(false); }
  };

  return open ? (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" data-testid="request-change-dialog">
      <div className="bg-zinc-950 border border-sky-500/30 rounded-2xl max-w-md w-full p-6">
        <div className="flex items-center gap-2 mb-2">
          <MessageSquare className="w-5 h-5 text-sky-300" />
          <h3 className="text-lg font-semibold text-white">Pedir alteração — prestação #{data.item.sequence}</h3>
        </div>
        <p className="text-zinc-400 text-sm mb-4">
          Actual: <strong>{new Date(data.item.due_date + "T00:00:00Z").toLocaleDateString("pt-PT")}</strong> · <strong>{Number(data.item.amount).toFixed(2)}€</strong>
        </p>
        <div className="space-y-3">
          <div>
            <Label className="text-zinc-300 text-sm">Nova data pretendida (opcional)</Label>
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="input-dark mt-1.5" data-testid="request-change-date" />
          </div>
          <div>
            <Label className="text-zinc-300 text-sm">Novo valor pretendido (opcional)</Label>
            <Input type="number" step="0.01" min="0" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder={`Actual: ${Number(data.item.amount).toFixed(2)}€`} className="input-dark mt-1.5" data-testid="request-change-amount" />
          </div>
          <div>
            <Label className="text-zinc-300 text-sm">Motivo (opcional, ajuda o admin)</Label>
            <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Ex: Salário atrasado, imprevisto..." className="input-dark mt-1.5" data-testid="request-change-reason" />
          </div>
        </div>
        <p className="text-[11px] text-zinc-500 mt-3">O admin pode aceitar, recusar ou fazer contra-proposta. Recebes email quando decidir.</p>
        <div className="flex justify-end gap-2 mt-5">
          <Button variant="outline" className="border-white/10 text-zinc-300" onClick={onClose}>Cancelar</Button>
          <Button onClick={submit} disabled={!canSubmit || submitting} className="bg-sky-500 hover:bg-sky-600 text-white" data-testid="request-change-submit">
            {submitting ? "..." : "Enviar pedido"}
          </Button>
        </div>
      </div>
    </div>
  ) : null;
};


const DecideCounterDialog = ({ data, open, onClose, onDecided }) => {
  const [submitting, setSubmitting] = useState(false);
  if (!data) return null;
  const { plan, request, action } = data;
  const isAccept = action === "accept";

  const submit = async () => {
    setSubmitting(true);
    try {
      await axios.post(
        `${API_URL}/api/users/me/tickets/${plan.id}/schedule/requests/${request.id}/decision`,
        { action },
        { withCredentials: true },
      );
      toast.success(isAccept ? "Contra-proposta aceite" : "Contra-proposta recusada");
      onDecided();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro");
    } finally { setSubmitting(false); }
  };

  return open ? (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
      <div className={`bg-zinc-950 border ${isAccept ? "border-emerald-500/30" : "border-red-500/30"} rounded-2xl max-w-md w-full p-6`}>
        <h3 className="text-lg font-semibold text-white mb-2">
          {isAccept ? "Aceitar contra-proposta?" : "Recusar contra-proposta?"}
        </h3>
        <p className="text-zinc-400 text-sm mb-4">
          {isAccept
            ? <>A prestação #{request.sequence} vai passar para <strong>{request.counter_date && new Date(request.counter_date + "T00:00:00Z").toLocaleDateString("pt-PT")}</strong>{request.counter_amount != null && <> · <strong>{Number(request.counter_amount).toFixed(2)}€</strong></>}.</>
            : <>A tua prestação #{request.sequence} vai manter-se com a data e valor originais.</>}
        </p>
        <div className="flex justify-end gap-2">
          <Button variant="outline" className="border-white/10 text-zinc-300" onClick={onClose}>Voltar</Button>
          <Button
            onClick={submit}
            disabled={submitting}
            className={isAccept ? "bg-emerald-500 hover:bg-emerald-600" : "bg-red-500 hover:bg-red-600"}
            data-testid="counter-decision-confirm"
          >
            {submitting ? "..." : isAccept ? "Confirmar aceitação" : "Confirmar recusa"}
          </Button>
        </div>
      </div>
    </div>
  ) : null;
};


export default function AccountPage() {
  const { user, loading, logout, updateProfile } = useUserAuth();
  const navigate = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [show, setShow] = useState(null);
  const [loadingTickets, setLoadingTickets] = useState(true);
  const [profileForm, setProfileForm] = useState(() => ({
    first_name: "",
    last_name: "",
    phone: "",
  }));
  const profileSyncedRef = React.useRef(false);
  const [savingProfile, setSavingProfile] = useState(false);
  const [changingPwd, setChangingPwd] = useState(false);
  const [pwd, setPwd] = useState({ current_password: "", new_password: "", confirm: "" });

  useEffect(() => {
    if (!loading && !user) navigate("/login");
  }, [loading, user, navigate]);

  useEffect(() => {
    if (user && !profileSyncedRef.current) {
      profileSyncedRef.current = true;
      setProfileForm({
        first_name: user.first_name || "",
        last_name: user.last_name || "",
        phone: user.phone || "",
      });
    }
  }, [user]);

  const fetchTickets = React.useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/users/me/tickets`, { withCredentials: true });
      setTickets(data.tickets || []);
      setShow(data.show || null);
    } catch (err) {
      toast.error("Erro ao carregar bilhetes");
    } finally {
      setLoadingTickets(false);
    }
  }, []);

  useEffect(() => {
    if (!user) return;
    fetchTickets();
  }, [user, fetchTickets]);

  const handleLogout = async () => {
    await logout();
    toast.success("Sessão terminada");
    navigate("/");
  };

  const handleProfileSave = async (e) => {
    e.preventDefault();
    setSavingProfile(true);
    try {
      await updateProfile(profileForm);
      toast.success("Perfil actualizado");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Erro ao guardar");
    } finally {
      setSavingProfile(false);
    }
  };

  const handleChangePwd = async (e) => {
    e.preventDefault();
    if (pwd.new_password !== pwd.confirm) {
      toast.error("As novas passwords não coincidem.");
      return;
    }
    if (pwd.new_password.length < 8) {
      toast.error("A nova password tem de ter pelo menos 8 caracteres.");
      return;
    }
    setChangingPwd(true);
    try {
      await axios.post(
        `${API_URL}/api/users/change-password`,
        { current_password: pwd.current_password, new_password: pwd.new_password },
        { withCredentials: true }
      );
      toast.success("Password actualizada");
      setPwd({ current_password: "", new_password: "", confirm: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Erro ao mudar password");
    } finally {
      setChangingPwd(false);
    }
  };

  const [resendingVerify, setResendingVerify] = useState(false);
  const [payingPlan, setPayingPlan] = useState(null);
  const [signingPlan, setSigningPlan] = useState(null);
  const [changeRequest, setChangeRequest] = useState(null); // {plan, item}
  const [decidingRequest, setDecidingRequest] = useState(null); // {plan, request, action}

  const handleResendVerify = async () => {
    setResendingVerify(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/users/send-verification`, {}, { withCredentials: true });
      toast.success(data.message || "Email enviado");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Não foi possível enviar agora");
    } finally {
      setResendingVerify(false);
    }
  };

  if (loading || !user) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-amber-400" />
      </div>
    );
  }

  const displayName = `${user.first_name || ""} ${user.last_name || ""}`.trim() || user.email;

  return (
    <div className="min-h-screen bg-black text-white">
      <header className="border-b border-white/5">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-5 flex items-center justify-between gap-4">
          <Link to="/" className="text-xl font-bold tracking-tight">Bilhetez</Link>
          <div className="flex items-center gap-3">
            <Link
              to="/"
              className="text-sm text-zinc-400 hover:text-white inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-white/10 hover:border-white/20 transition-colors"
              data-testid="account-back-home"
            >
              <Home className="w-4 h-4" />
              <span className="hidden sm:inline">Voltar ao site</span>
            </Link>
            <div className="text-right hidden sm:block">
              <p className="text-sm font-medium" data-testid="account-name">{displayName}</p>
              <p className="text-xs text-zinc-500">{user.email}</p>
            </div>
            <button
              onClick={handleLogout}
              className="text-sm text-zinc-400 hover:text-white inline-flex items-center gap-1.5"
              data-testid="account-logout"
            >
              <LogOut className="w-4 h-4" />
              <span className="hidden sm:inline">Sair</span>
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-1">Olá, {user.first_name || "👋"}</h1>
          <p className="text-zinc-400 text-sm">Gerencie os seus bilhetes e dados pessoais.</p>
        </div>

        {!user.email_verified && (
          <div
            className="mb-6 bg-amber-500/10 border border-amber-500/30 rounded-xl px-4 py-3 flex items-start sm:items-center gap-3 flex-wrap"
            data-testid="verify-banner"
          >
            <AlertCircle className="w-5 h-5 text-amber-400 shrink-0 mt-0.5 sm:mt-0" />
            <div className="flex-1 min-w-0">
              <p className="text-sm text-amber-100 font-medium">Confirme o seu email</p>
              <p className="text-xs text-amber-200/70">Enviámos um link para <strong>{user.email}</strong>. Pode continuar a usar a conta normalmente — a confirmação ajuda a proteger a sua conta.</p>
            </div>
            <Button
              onClick={handleResendVerify}
              disabled={resendingVerify}
              size="sm"
              variant="outline"
              className="border-amber-500/40 text-amber-200 hover:bg-amber-500/15 hover:text-amber-100 shrink-0"
              data-testid="verify-resend-btn"
            >
              {resendingVerify ? "A enviar..." : "Reenviar email"}
            </Button>
          </div>
        )}

        <Tabs defaultValue="tickets" className="space-y-6">
          <TabsList className="bg-zinc-900 border border-white/10 p-1">
            <TabsTrigger value="tickets" className="text-zinc-300 hover:text-white data-[state=active]:bg-amber-500 data-[state=active]:text-black gap-2" data-testid="tab-tickets">
              <TicketIcon className="w-4 h-4" /> Bilhetes
              <span className="ml-1 text-xs bg-black/20 px-1.5 py-0.5 rounded">{tickets.length}</span>
            </TabsTrigger>
            <TabsTrigger value="plans" className="text-zinc-300 hover:text-white data-[state=active]:bg-amber-500 data-[state=active]:text-black gap-2" data-testid="tab-plans">
              <CreditCard className="w-4 h-4" /> Planos
              {(() => {
                const c = tickets.filter((t) => t.is_installment_plan && t.plan_status !== "completed").length;
                return c > 0 ? <span className="ml-1 text-xs bg-black/20 px-1.5 py-0.5 rounded">{c}</span> : null;
              })()}
            </TabsTrigger>
            <button
              type="button"
              onClick={() => navigate("/conta/familia")}
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium text-zinc-300 hover:bg-cyan-500/10 hover:text-cyan-200 transition-colors"
              data-testid="tab-family"
            >
              <Baby className="w-4 h-4" /> Menores
            </button>
            <TabsTrigger value="profile" className="text-zinc-300 hover:text-white data-[state=active]:bg-amber-500 data-[state=active]:text-black gap-2" data-testid="tab-profile">
              <User className="w-4 h-4" /> Perfil
            </TabsTrigger>
            <TabsTrigger value="security" className="text-zinc-300 hover:text-white data-[state=active]:bg-amber-500 data-[state=active]:text-black gap-2" data-testid="tab-security">
              <Lock className="w-4 h-4" /> Segurança
            </TabsTrigger>
          </TabsList>

          {/* ─────────────── Bilhetes ─────────────── */}
          <TabsContent value="tickets">
            {loadingTickets ? (
              <div className="py-12 text-center text-zinc-500"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
            ) : tickets.length === 0 ? (
              <div className="bg-zinc-950 border border-white/10 rounded-2xl p-12 text-center" data-testid="tickets-empty">
                <TicketIcon className="w-12 h-12 text-zinc-700 mx-auto mb-4" />
                <h3 className="text-lg font-semibold mb-1">Ainda não tem bilhetes</h3>
                <p className="text-zinc-400 text-sm mb-6">Os seus bilhetes aparecerão aqui automaticamente após a compra.</p>
                <Link to="/" className="btn-primary inline-flex items-center gap-2" data-testid="tickets-empty-cta">
                  Ver eventos disponíveis <ExternalLink className="w-4 h-4" />
                </Link>
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 gap-4" data-testid="tickets-grid">
                {tickets.map((t) => (
                  <div key={t.id} className="bg-zinc-950 border border-white/10 rounded-2xl overflow-hidden hover:border-amber-500/40 transition-colors" data-testid={`ticket-${t.ticket_number}`}>
                    <div className="p-5 border-b border-white/5">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="text-zinc-500 text-xs uppercase tracking-wider">{t.ticket_type_name || "Bilhete"}</p>
                          <p className="font-mono text-sm text-amber-400 mt-0.5 truncate">{t.ticket_number}</p>
                        </div>
                        <StatusBadge status={t.payment_status} />
                      </div>
                      <p className="text-white font-semibold mt-2 truncate">{t.first_name} {t.last_name}</p>
                      {!t.is_purchaser && t.is_attendee && (
                        <p className="text-xs text-zinc-500 mt-1">Bilhete em seu nome (comprado por outra pessoa)</p>
                      )}
                    </div>
                    <div className="p-5 space-y-2 text-sm">
                      <div className="flex items-center gap-2 text-zinc-300">
                        <Calendar className="w-4 h-4 text-zinc-500" />
                        <span>{t.session_date || "—"}</span>
                      </div>
                      <div className="flex items-center gap-2 text-zinc-300">
                        <Clock className="w-4 h-4 text-zinc-500" />
                        <span>{t.session_time || "—"}</span>
                      </div>
                      {show?.location && (
                        <div className="text-zinc-500 text-xs pt-1">{show.location}</div>
                      )}

                      {/* ─── Installment plan progress ─────────────────── */}
                      {t.is_installment_plan && t.plan_status !== "completed" && (
                        <div className="mt-3 pt-3 border-t border-white/5 space-y-2" data-testid={`ticket-plan-${t.ticket_number}`}>
                          <div className="flex items-center justify-between text-xs">
                            <span className="text-amber-300 font-medium flex items-center gap-1.5"><CreditCard className="w-3.5 h-3.5" /> Plano em curso</span>
                            <span className="text-zinc-400">{t.total_paid.toFixed(2)}€ / {t.total_due.toFixed(2)}€</span>
                          </div>
                          <PlanProgressBar percent={t.progress_percent} />
                          {t.is_purchaser && t.remaining_due > 0 && (
                            <Button
                              size="sm"
                              onClick={() => setPayingPlan(t)}
                              className="btn-primary w-full mt-2"
                              data-testid={`pay-next-${t.ticket_number}`}
                            >
                              Pagar próxima prestação
                            </Button>
                          )}
                        </div>
                      )}

                      {t.checked_in ? (
                        <div className="flex items-center gap-1.5 text-emerald-400 text-xs pt-2">
                          <CheckCircle2 className="w-3.5 h-3.5" /> Check-in efectuado
                        </div>
                      ) : (t.payment_status === "pending" && !t.is_installment_plan) ? (
                        <div className="flex items-center gap-1.5 text-amber-300 text-xs pt-2">
                          <AlertCircle className="w-3.5 h-3.5" /> Pagamento pendente
                        </div>
                      ) : null}
                    </div>
                    {t.qr_issued && t.ticket_number && (
                      <div className="px-5 pb-5">
                        <Link to={`/ticket/${t.ticket_number}`} className="block text-center bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg py-2 text-sm font-medium" data-testid={`view-ticket-${t.ticket_number}`}>
                          Ver bilhete & QR Code
                        </Link>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* ─────────────── Planos de Pagamento ─────────────── */}
          <TabsContent value="plans">
            {(() => {
              const plans = tickets.filter((t) => t.is_installment_plan);
              if (loadingTickets) return <div className="py-12 text-center"><Loader2 className="w-6 h-6 animate-spin text-amber-400 mx-auto" /></div>;
              if (plans.length === 0) {
                return (
                  <div className="bg-zinc-950 border border-white/10 rounded-2xl p-12 text-center" data-testid="plans-empty">
                    <CreditCard className="w-12 h-12 text-zinc-700 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold mb-1">Sem planos em curso</h3>
                    <p className="text-zinc-400 text-sm mb-6">Compre um bilhete e escolha &quot;Pagar a prazo&quot; para criar um plano.</p>
                    <Link to="/" className="btn-primary inline-flex items-center gap-2"><ExternalLink className="w-4 h-4" /> Ver eventos</Link>
                  </div>
                );
              }
              const active = plans.filter((p) => p.plan_status !== "completed");
              const completed = plans.filter((p) => p.plan_status === "completed");
              return (
                <div className="space-y-6">
                  {active.length > 0 && (
                    <div className="space-y-3">
                      <h3 className="text-sm font-semibold text-zinc-300 uppercase tracking-wider">Em curso ({active.length})</h3>
                      <div className="grid sm:grid-cols-2 gap-4" data-testid="plans-active">
                        {active.map((p) => (
                          <div key={p.id} className="bg-zinc-950 border border-amber-500/30 rounded-2xl overflow-hidden" data-testid={`plan-${p.ticket_number}`}>
                            <div className="p-5 border-b border-white/5">
                              <div className="flex items-start justify-between gap-3">
                                <div className="min-w-0">
                                  <p className="text-zinc-500 text-xs uppercase tracking-wider">{p.ticket_type_name}</p>
                                  <p className="font-mono text-sm text-amber-400 mt-0.5 truncate">{p.ticket_number}</p>
                                </div>
                                <span className="text-xs px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-300 border border-amber-500/30 font-medium">
                                  {p.progress_percent.toFixed(0)}% pago
                                </span>
                              </div>
                              <p className="text-white font-semibold mt-2 truncate">{p.first_name} {p.last_name}</p>
                              <p className="text-xs text-zinc-500">{p.session_date} · {p.session_time}</p>
                            </div>
                            <div className="p-5 space-y-3">
                              <div>
                                <div className="flex justify-between text-xs mb-1.5">
                                  <span className="text-zinc-400">Progresso</span>
                                  <span className="text-zinc-200 font-mono">{p.total_paid.toFixed(2)}€ / {p.total_due.toFixed(2)}€</span>
                                </div>
                                <PlanProgressBar percent={p.progress_percent} />
                                <p className="text-xs text-amber-300 mt-1.5">Falta pagar: <strong>{p.remaining_due.toFixed(2)}€</strong></p>
                              </div>

                              {(p.payment_schedule || []).length > 0 && (
                                <div className="bg-sky-500/5 border border-sky-500/20 rounded-lg p-3" data-testid={`plan-schedule-${p.ticket_number}`}>
                                  <div className="flex items-center justify-between mb-2">
                                    <p className="text-sky-200 text-xs font-semibold flex items-center gap-1.5">
                                      <FileSignature className="w-3.5 h-3.5" /> Cronograma acordado
                                    </p>
                                    {p.payment_agreement_digital_signature ? (
                                      <span className="text-[10px] px-2 py-0.5 rounded bg-emerald-500/15 text-emerald-300 border border-emerald-500/30">Assinado ✓</span>
                                    ) : p.is_purchaser && (
                                      <button
                                        type="button"
                                        onClick={() => setSigningPlan(p)}
                                        className="text-[10px] px-2 py-0.5 rounded bg-sky-500 text-white font-medium hover:bg-sky-600"
                                        data-testid={`plan-sign-btn-${p.ticket_number}`}
                                      >
                                        Confirmar plano
                                      </button>
                                    )}
                                  </div>
                                  <div className="space-y-1">
                                    {p.payment_schedule.map((s) => {
                                      const activeReq = (p.schedule_change_requests || []).find(
                                        (r) => r.item_id === s.id && (r.status === "pending" || r.status === "countered"),
                                      );
                                      return (
                                        <div key={s.id || s.sequence} className="bg-white/[0.03] rounded px-2 py-1">
                                          <div className="grid grid-cols-[24px_1fr_80px_60px_28px] items-center gap-2 text-xs">
                                            <span className="text-sky-300 font-semibold">#{s.sequence}</span>
                                            <span className="text-zinc-200">{s.due_date ? new Date(s.due_date + "T00:00:00Z").toLocaleDateString("pt-PT") : "—"}</span>
                                            <span className="text-amber-300 font-mono text-right">{Number(s.amount || 0).toFixed(2)}€</span>
                                            <span className={`text-[10px] px-1.5 py-0.5 rounded text-center ${s.paid ? "bg-emerald-500/15 text-emerald-300" : "bg-zinc-500/15 text-zinc-400"}`}>
                                              {s.paid ? "Pago" : "Pend."}
                                            </span>
                                            {!s.paid && !activeReq && p.is_purchaser ? (
                                              <button
                                                type="button"
                                                onClick={() => setChangeRequest({ plan: p, item: s })}
                                                className="text-sky-300 hover:text-sky-100"
                                                title="Pedir alteração de prazo/valor"
                                                data-testid={`user-request-change-${p.ticket_number}-${s.sequence}`}
                                              >
                                                <Pencil className="w-3 h-3" />
                                              </button>
                                            ) : <span />}
                                          </div>
                                          {activeReq && (
                                            <div className={`mt-1.5 rounded px-2 py-1 text-[10px] border ${
                                              activeReq.status === "countered" ? "bg-sky-500/10 border-sky-500/30 text-sky-200" : "bg-amber-500/10 border-amber-500/30 text-amber-200"
                                            }`} data-testid={`user-request-status-${activeReq.id}`}>
                                              {activeReq.status === "pending" && (
                                                <span>⏳ Pedido enviado ao admin{activeReq.proposed_date && `: nova data ${new Date(activeReq.proposed_date + "T00:00:00Z").toLocaleDateString("pt-PT")}`}</span>
                                              )}
                                              {activeReq.status === "countered" && (
                                                <div className="flex items-center justify-between gap-2 flex-wrap">
                                                  <span>
                                                    💬 Contra-proposta: {activeReq.counter_date && new Date(activeReq.counter_date + "T00:00:00Z").toLocaleDateString("pt-PT")}
                                                    {activeReq.counter_amount != null && ` · ${Number(activeReq.counter_amount).toFixed(2)}€`}
                                                    {activeReq.admin_reason && <em className="block text-zinc-400 mt-0.5">&ldquo;{activeReq.admin_reason}&rdquo;</em>}
                                                  </span>
                                                  {p.is_purchaser && (
                                                    <div className="flex gap-1">
                                                      <button
                                                        type="button"
                                                        onClick={() => setDecidingRequest({ plan: p, request: activeReq, action: "accept" })}
                                                        className="px-2 py-0.5 rounded bg-emerald-500 text-white text-[10px] font-medium hover:bg-emerald-600"
                                                        data-testid={`counter-accept-${activeReq.id}`}
                                                      >
                                                        Aceitar
                                                      </button>
                                                      <button
                                                        type="button"
                                                        onClick={() => setDecidingRequest({ plan: p, request: activeReq, action: "reject" })}
                                                        className="px-2 py-0.5 rounded bg-red-500 text-white text-[10px] font-medium hover:bg-red-600"
                                                        data-testid={`counter-reject-${activeReq.id}`}
                                                      >
                                                        Recusar
                                                      </button>
                                                    </div>
                                                  )}
                                                </div>
                                              )}
                                            </div>
                                          )}
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                              )}

                              {p.installments_count > 0 && (
                                <div>
                                  <p className="text-xs text-zinc-400 mb-1.5">Histórico ({p.installments_count})</p>
                                  <div className="space-y-1 max-h-32 overflow-y-auto">
                                    {p.installments.slice().reverse().map((inst, idx) => (
                                      <div key={inst.id || idx} className="flex justify-between text-xs bg-white/[0.02] rounded px-2 py-1">
                                        <span className="text-zinc-400">
                                          {new Date(inst.paid_at).toLocaleDateString("pt-PT")} · {inst.payment_method}
                                        </span>
                                        <span className="text-emerald-300 font-mono">+{inst.amount.toFixed(2)}€</span>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}

                              {p.is_purchaser && (
                                <Button
                                  onClick={() => setPayingPlan(p)}
                                  className="btn-primary w-full"
                                  data-testid={`plan-pay-${p.ticket_number}`}
                                >
                                  <CreditCard className="w-4 h-4 mr-2" />
                                  Pagar prestação
                                </Button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {completed.length > 0 && (
                    <div className="space-y-3">
                      <h3 className="text-sm font-semibold text-zinc-300 uppercase tracking-wider">Concluídos ({completed.length})</h3>
                      <div className="space-y-2" data-testid="plans-completed">
                        {completed.map((p) => (
                          <div key={p.id} className="bg-zinc-950 border border-emerald-500/30 rounded-xl px-4 py-3 flex items-center justify-between flex-wrap gap-2">
                            <div>
                              <p className="text-sm font-medium text-white">{p.first_name} {p.last_name} · <span className="font-mono text-amber-400">{p.ticket_number}</span></p>
                              <p className="text-xs text-zinc-500">{p.ticket_type_name} · {p.installments_count} prestaç{p.installments_count === 1 ? "ão" : "ões"}</p>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="text-xs text-emerald-300">{p.total_due.toFixed(2)}€ pagos</span>
                              {p.qr_issued && (
                                <Link to={`/ticket/${p.ticket_number}`} className="text-xs px-2 py-1 rounded bg-white/5 hover:bg-white/10 text-zinc-200">Ver bilhete →</Link>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}
          </TabsContent>

          {/* ─────────────── Perfil ─────────────── */}
          <TabsContent value="profile">
            <form onSubmit={handleProfileSave} className="bg-zinc-950 border border-white/10 rounded-2xl p-6 max-w-xl space-y-4" data-testid="profile-form">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-zinc-300">Primeiro nome</Label>
                  <Input value={profileForm.first_name} onChange={(e) => setProfileForm((p) => ({ ...p, first_name: e.target.value }))} className="input-dark mt-1.5" data-testid="profile-first-name" />
                </div>
                <div>
                  <Label className="text-zinc-300">Último nome</Label>
                  <Input value={profileForm.last_name} onChange={(e) => setProfileForm((p) => ({ ...p, last_name: e.target.value }))} className="input-dark mt-1.5" data-testid="profile-last-name" />
                </div>
              </div>
              <div>
                <Label className="text-zinc-300 flex items-center gap-1.5"><Mail className="w-3.5 h-3.5" /> Email</Label>
                <Input value={user.email} disabled className="input-dark mt-1.5 opacity-60 cursor-not-allowed" />
                <p className="text-xs text-zinc-500 mt-1">O email não pode ser alterado.</p>
              </div>
              <div>
                <Label className="text-zinc-300 flex items-center gap-1.5"><Phone className="w-3.5 h-3.5" /> Telefone</Label>
                <Input value={profileForm.phone} onChange={(e) => setProfileForm((p) => ({ ...p, phone: e.target.value }))} className="input-dark mt-1.5" placeholder="+351 ..." data-testid="profile-phone" />
              </div>
              <Button type="submit" disabled={savingProfile} className="btn-primary" data-testid="profile-save">
                {savingProfile ? "A guardar..." : "Guardar alterações"}
              </Button>
            </form>
          </TabsContent>

          {/* ─────────────── Segurança ─────────────── */}
          <TabsContent value="security">
            <form onSubmit={handleChangePwd} className="bg-zinc-950 border border-white/10 rounded-2xl p-6 max-w-xl space-y-4" data-testid="pwd-form">
              <h3 className="text-lg font-semibold">Mudar password</h3>
              <div>
                <Label className="text-zinc-300">Password actual</Label>
                <Input type="password" required value={pwd.current_password} onChange={(e) => setPwd((p) => ({ ...p, current_password: e.target.value }))} className="input-dark mt-1.5" autoComplete="current-password" data-testid="pwd-current" />
              </div>
              <div>
                <Label className="text-zinc-300">Nova password</Label>
                <Input type="password" required value={pwd.new_password} onChange={(e) => setPwd((p) => ({ ...p, new_password: e.target.value }))} className="input-dark mt-1.5" placeholder="Mínimo 8 caracteres" autoComplete="new-password" data-testid="pwd-new" />
              </div>
              <div>
                <Label className="text-zinc-300">Confirmar nova password</Label>
                <Input type="password" required value={pwd.confirm} onChange={(e) => setPwd((p) => ({ ...p, confirm: e.target.value }))} className="input-dark mt-1.5" autoComplete="new-password" data-testid="pwd-confirm" />
              </div>
              <Button type="submit" disabled={changingPwd || !pwd.current_password || !pwd.new_password} className="btn-primary" data-testid="pwd-submit">
                {changingPwd ? "A actualizar..." : "Mudar password"}
              </Button>
            </form>
          </TabsContent>
        </Tabs>

        <PayInstallmentDialog
          ticket={payingPlan}
          open={!!payingPlan}
          onClose={() => setPayingPlan(null)}
          onPaid={() => { setPayingPlan(null); }}
        />
        <SignPlanDialog
          plan={signingPlan}
          open={!!signingPlan}
          onClose={() => setSigningPlan(null)}
          onSigned={() => { setSigningPlan(null); fetchTickets(); }}
        />
        <RequestChangeDialog
          data={changeRequest}
          open={!!changeRequest}
          onClose={() => setChangeRequest(null)}
          onSent={() => { setChangeRequest(null); fetchTickets(); }}
        />
        <DecideCounterDialog
          data={decidingRequest}
          open={!!decidingRequest}
          onClose={() => setDecidingRequest(null)}
          onDecided={() => { setDecidingRequest(null); fetchTickets(); }}
        />
      </main>
    </div>
  );
}
